import React from 'react';
import { Button } from 'primereact/button';
import { LectureLecturer } from '../../../../../api/types/lecturers/lectures/lecture-lecturer';

interface MyLectureActionsProps {
  lecture: LectureLecturer;
  onStartLecture: (lecture: LectureLecturer) => void;
  onFinishLecture: (lecture: LectureLecturer) => void;
  onUpdateAttendance: (lecture: LectureLecturer) => void;
}

// Check if start button should be enabled
const canStartLecture = (lecture: LectureLecturer): boolean => {
  // If lecture already started, disable the start button
  if (lecture.actualStartTime !== null) {
    return false;
  }

  const now = new Date();
  const startTime = new Date(lecture.startTime);
  const fiveMinutesBefore = new Date(startTime.getTime() - 60 * 60 * 1000);
  const fifteenMinutesAfter = new Date(startTime.getTime() + 15 * 60 * 1000);

  return now >= fiveMinutesBefore && now <= fifteenMinutesAfter;
};

// Check if finish button should be enabled
const canFinishLecture = (lecture: LectureLecturer): boolean => {
  // If lecture already finished, disable the finish button
  if (lecture.actualEndTime !== null) {
    return false;
  }

  const now = new Date();
  const endTime = new Date(lecture.endTime);
  const tenMinutesAfter = new Date(endTime.getTime() + 10 * 60 * 1000);

  return now <= tenMinutesAfter;
};

export const MyLectureActions: React.FC<MyLectureActionsProps> = ({
  lecture,
  onStartLecture,
  onFinishLecture,
  onUpdateAttendance,
}) => {
  const hasStarted = lecture.actualStartTime !== null;
  const hasFinished = lecture.actualEndTime !== null;
  const canStart = canStartLecture(lecture);
  const canFinish = canFinishLecture(lecture);
  const canUpdateAttendance = hasStarted; // Can update attendance after lecture has started

  const startTooltip = canStart
    ? 'לחץ להתחלת ההרצאה'
    : 'יש להחיל הרצאה עד שעה לפני ועד 15 דקות אחרי';

  const finishTooltip = canFinish
    ? 'לחץ לסיום ההרצאה'
    : 'ניתן לסגור את ההרצאה עד עשר דקות אחרי הזמן המתוכנן';

  const updateAttendanceTooltip = canUpdateAttendance
    ? 'לחץ לעדכון נתוני נוכחות'
    : 'ניתן לעדכן נוכחות רק לאחר התחלת ההרצאה';

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <Button
        label="התחל"
        size="small"
        tooltip={startTooltip}
        tooltipOptions={{
          showOnDisabled: true,
        }}
        severity={hasStarted ? 'secondary' : 'success'}
        disabled={!canStart}
        onClick={() => onStartLecture(lecture)}
        style={{
          minWidth: '60px',
          ...(hasStarted && {
            backgroundColor: '#6c757d',
            borderColor: '#6c757d',
            color: '#fff',
            opacity: 0.6,
          }),
        }}
      />

      <Button
        label="סיום"
        size="small"
        tooltip={finishTooltip}
        tooltipOptions={{
          showOnDisabled: true,
        }}
        severity={hasFinished ? 'secondary' : 'danger'}
        disabled={!canFinish}
        onClick={() => onFinishLecture(lecture)}
        style={{
          minWidth: '60px',
          ...(hasFinished && {
            backgroundColor: '#6c757d',
            borderColor: '#6c757d',
            color: '#fff',
            opacity: 0.6,
          }),
        }}
      />

      <Button
        label="עדכון נוכחות"
        size="small"
        tooltip={updateAttendanceTooltip}
        tooltipOptions={{
          showOnDisabled: true,
        }}
        severity="info"
        disabled={!canUpdateAttendance}
        onClick={() => onUpdateAttendance(lecture)}
        style={{
          minWidth: '90px',
        }}
      />
    </div>
  );
};
